import type { OrbColor } from "./VariableState";
import type { Condition } from "./Condition";

/**
 * カードの種類
 */
export type CardType =
  | "forward"
  | "turn"
  | "addToOrb"
  | "multiplyOrb"
  | "if"
  | "while"
  | "call"
  | "action";

/**
 * callカードが呼び出すサブブックのID
 */
export type SubbookId = "subbook-a" | "subbook-b" | "subbook-c";

/**
 * 方向転換の方向
 */
export type TurnDirection = "right" | "left" | "back";

/** 方向転換の全方向（表示順） */
export const TURN_DIRECTIONS: readonly TurnDirection[] = [
  "right",
  "left",
  "back",
];

/**
 * TurnDirection → value の数値エンコーディング
 *
 * カードの value フィールドに方向を保存するためのマッピング。
 * 値は単なるID（意味のない正の整数）。
 * 変更すると既存のカードデータとの互換性が失われる可能性があるため注意。
 */
export const TURN_DIRECTION_VALUES: Record<TurnDirection, number> = {
  right: 1,
  left: 2,
  back: 3,
};

/** value → TurnDirection の逆引き（TURN_DIRECTION_VALUES から自動生成） */
export const TURN_VALUE_TO_DIRECTION = Object.fromEntries(
  Object.entries(TURN_DIRECTION_VALUES).map(([k, v]) => [v, k])
) as Record<number, TurnDirection>;

/** 各方向の表示ラベル */
export const TURN_DIRECTION_LABELS: Record<TurnDirection, string> = {
  right: "右向く",
  left: "左向く",
  back: "後ろ向く",
};

/** 方向転換カードのコスト（全方向共通） */
export const TURN_COST = 1;

/**
 * 条件分岐/繰り返しの1つの分岐
 *
 * - condition あり: if（条件が真のとき実行）/ while（条件が真の間繰り返す）
 * - condition なし: else（常に真、必ず最後に配置）
 */
export interface Branch {
  id: string;
  condition?: Condition;
  cards: Card[];
}

/**
 * カードのインターフェース
 */
export interface Card {
  id: string;
  type: CardType;
  label: string;
  cost: number;
  /** 前進カードの移動マス数 / 足す・かけるの値 / 方向転換の方向 */
  value?: number;
  /** オーブ色: forward(変数参照時) / addToOrb / multiplyOrb で使用 */
  orbColor?: OrbColor;
  /** if/while カードの分岐群 */
  branches?: Branch[];
  /** callカードが呼び出すサブブックのID */
  subbookId?: SubbookId;
}

/** 前進カードの1マスあたりのコスト */
export const FORWARD_COST_PER_STEP = 2;

/** 前進カードが変数参照モードの場合の固定コスト */
export const FORWARD_ORB_COST = 2;

/** 足すカードの1あたりのコスト */
export const ADD_COST_PER_VALUE = 1;

/** かけるカードの1あたりのコスト */
export const MULTIPLY_COST_PER_VALUE = 1;

/** 各ブランチのコスト（if/else それぞれ1MP） */
export const BRANCH_COST = 1;

/** while カードのベースコスト（ループは強力な構文なので if より高い） */
export const WHILE_BASE_COST = 2;

/** call カードのコスト（サブブック呼び出し1回あたり） */
export const CALL_COST = 2;

/**
 * @deprecated BRANCH_COST を使用してください
 */
export const IF_BASE_COST = BRANCH_COST;

/**
 * 利用可能なカード定義
 * 注: forward / addToOrb / multiplyOrb のコストはvalueに応じて動的に計算される
 */
export const CARD_DEFINITIONS: Record<CardType, Omit<Card, "id">> = {
  forward: {
    type: "forward",
    label: "前進",
    cost: FORWARD_COST_PER_STEP,
    value: 1,
  },
  turn: {
    type: "turn",
    label: "方向転換",
    cost: TURN_COST,
    value: TURN_DIRECTION_VALUES.right,
  },
  addToOrb: {
    type: "addToOrb",
    label: "足す",
    cost: ADD_COST_PER_VALUE,
    value: 1,
    orbColor: "green",
  },
  multiplyOrb: {
    type: "multiplyOrb",
    label: "かける",
    cost: MULTIPLY_COST_PER_VALUE * 2,
    value: 2,
    orbColor: "green",
  },
  if: {
    type: "if",
    label: "もし",
    cost: BRANCH_COST,
  },
  while: {
    type: "while",
    label: "繰り返す",
    cost: WHILE_BASE_COST,
  },
  call: {
    type: "call",
    label: "よびだす",
    cost: CALL_COST,
  },
  action: {
    type: "action",
    label: "アクション",
    cost: 1,
  },
};

/** SubbookId → 表示サフィックス（"A" / "B" / "C"） */
export const SUBBOOK_LABEL_SUFFIX: Record<SubbookId, string> = {
  "subbook-a": "A",
  "subbook-b": "B",
  "subbook-c": "C",
};

/**
 * 新しいカードを生成
 * @param type カードの種類
 * @param value 前進の歩数 / 足す・かけるの値（デフォルト: カード定義の初期値）
 * @param orbColor オーブ色（デフォルト: カード定義の初期値）
 * @param subbookId callカードが呼び出すサブブックのID
 */
export function createCard(
  type: CardType,
  value?: number,
  orbColor?: OrbColor,
  subbookId?: SubbookId
): Card {
  const base = CARD_DEFINITIONS[type];

  if (type === "call") {
    const suffix = subbookId ? ` ${SUBBOOK_LABEL_SUFFIX[subbookId]}` : "";
    return {
      id: crypto.randomUUID(),
      type,
      label: `よびだす${suffix}`,
      cost: CALL_COST,
      subbookId,
    };
  }

  if (type === "if") {
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: BRANCH_COST,
      branches: [
        {
          id: crypto.randomUUID(),
          condition: { type: "wallAhead" },
          cards: [],
        },
      ],
    };
  }

  if (type === "while") {
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: WHILE_BASE_COST,
      branches: [
        {
          id: crypto.randomUUID(),
          condition: { type: "noWallAhead" },
          cards: [],
        },
      ],
    };
  }

  if (type === "turn") {
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: TURN_COST,
      value: value ?? TURN_DIRECTION_VALUES.right,
    };
  }

  if (type === "forward") {
    if (orbColor) {
      return {
        id: crypto.randomUUID(),
        type,
        label: base.label,
        cost: FORWARD_ORB_COST,
        orbColor,
      };
    }
    const steps = value ?? 1;
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: getForwardCost(steps),
      value: steps,
    };
  }

  if (type === "addToOrb") {
    const v = value ?? 1;
    const color = orbColor ?? "green";
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: getAddCost(v),
      value: v,
      orbColor: color,
    };
  }

  if (type === "multiplyOrb") {
    const v = value ?? 2;
    const color = orbColor ?? "green";
    return {
      id: crypto.randomUUID(),
      type,
      label: base.label,
      cost: getMultiplyCost(v),
      value: v,
      orbColor: color,
    };
  }

  return {
    id: crypto.randomUUID(),
    ...base,
  };
}

/**
 * 前進カードのコストを計算
 */
export function getForwardCost(steps: number): number {
  return Math.abs(steps) * FORWARD_COST_PER_STEP;
}

/**
 * 足すカードのコストを計算
 */
export function getAddCost(value: number): number {
  return Math.abs(value) * ADD_COST_PER_VALUE;
}

/**
 * かけるカードのコストを計算
 */
export function getMultiplyCost(value: number): number {
  return Math.abs(value) * MULTIPLY_COST_PER_VALUE;
}

/** コスト計算の再帰深さ上限（極端なネストでのスタックオーバーフロー防止） */
const MAX_COST_DEPTH = 20;

/**
 * カードのコストを取得（動的計算対応）
 *
 * if カードの場合、ブランチ数 × BRANCH_COST + 全ブランチの子カードのコスト合計を再帰的に計算する。
 * while カードの場合、WHILE_BASE_COST + ボディ内カードのコスト合計（1回分）。
 */
export function getCardCost(card: Card): number {
  return getCardCostRecursive(card, 0);
}

function getCardCostRecursive(card: Card, depth: number): number {
  if (depth > MAX_COST_DEPTH) {
    return card.cost;
  }

  if (card.type === "if") {
    if (!card.branches || card.branches.length === 0) {
      return BRANCH_COST;
    }
    const branchBaseCost = card.branches.length * BRANCH_COST;
    const childrenCost = card.branches.reduce(
      (sum, branch) =>
        sum +
        branch.cards.reduce(
          (s, c) => s + getCardCostRecursive(c, depth + 1),
          0
        ),
      0
    );
    return branchBaseCost + childrenCost;
  }
  if (card.type === "while") {
    if (!card.branches || card.branches.length === 0) {
      return WHILE_BASE_COST;
    }
    const childrenCost = card.branches[0].cards.reduce(
      (sum, c) => sum + getCardCostRecursive(c, depth + 1),
      0
    );
    return WHILE_BASE_COST + childrenCost;
  }
  if (card.type === "call") {
    return CALL_COST;
  }
  if (card.type === "forward") {
    if (card.orbColor) {
      return FORWARD_ORB_COST;
    }
    if (card.value !== undefined) {
      return getForwardCost(card.value);
    }
  }
  if (card.type === "addToOrb" && card.value !== undefined) {
    return getAddCost(card.value);
  }
  if (card.type === "multiplyOrb" && card.value !== undefined) {
    return getMultiplyCost(card.value);
  }
  return card.cost;
}

/**
 * サブブック定義
 *
 * カードの組み合わせを名前付きで再利用可能にする。
 * Phase 10 では1スロット固定（パラメータなし）。
 * 複数スロット対応時はCard型にsubbookIdを持たせてExecutorで検索する設計に変更予定。
 */
export interface SubbookDefinition {
  id: string;
  name: string;
  cards: Card[];
}

/**
 * サブブックボディのコスト合計を計算
 */
export function calcSubbookBodyCost(cards: Card[]): number {
  return cards.reduce((sum, card) => sum + getCardCost(card), 0);
}
